import apiClient from './api/client';
import ENDPOINTS from './api/endpoints';

// Normalise any status string to UPPERCASE before sending to the Java enum
const toStatusEnum = (status) => status?.toUpperCase();

const purchaseOrderService = {
  /**
   * Fetch all purchase orders with optional filters.
   * Supports: branchId, status, supplierId, search, fromDate, toDate, page, size
   */
  async getAll(filters = {}) {
    const params = {};
    if (filters.branchId)     params.branchId    = filters.branchId;
    if (filters.status)       params.status      = toStatusEnum(filters.status);
    if (filters.supplierId)   params.supplierId  = filters.supplierId;
    if (filters.search)       params.search      = filters.search;
    if (filters.fromDate)     params.fromDate    = filters.fromDate;
    if (filters.toDate)       params.toDate      = filters.toDate;
    if (filters.page != null) params.page        = filters.page;
    if (filters.size != null) params.size        = filters.size;

    return apiClient.get(ENDPOINTS.PURCHASE_ORDERS.GET_ALL, { params });
  },

  /** Fetch a single purchase order by ID. */
  async getById(id) {
    return apiClient.get(ENDPOINTS.PURCHASE_ORDERS.GET_BY_ID(id));
  },

  /**
   * Create a new purchase order.
   * Status is normalised to uppercase automatically.
   */
  async create(data) {
    return apiClient.post(ENDPOINTS.PURCHASE_ORDERS.CREATE, {
      ...data,
      status: toStatusEnum(data.status),
    });
  },

  /**
   * Update an existing purchase order — same payload shape as create().
   * Status is normalised to uppercase automatically.
   */
  async update(id, data) {
    return apiClient.put(ENDPOINTS.PURCHASE_ORDERS.UPDATE(id), {
      ...data,
      status: toStatusEnum(data.status),
    });
  },

  /** Delete a purchase order (draft only). */
  async delete(id) {
    return apiClient.delete(ENDPOINTS.PURCHASE_ORDERS.DELETE(id));
  },

  /**
   * Update PO status.
   * Accepts any case — normalised to uppercase before sending.
   * Transitions: draft → pending → approved → received / partially_received
   *              any non-terminal → cancelled
   */
  async updateStatus(id, status) {
    return apiClient.put(ENDPOINTS.PURCHASE_ORDERS.UPDATE_STATUS(id), {
      status: toStatusEnum(status),
    });
  },

  /**
   * Mark items as received.
   * receivedItems: [{ lineItemId, quantity }]
   */
async receivePO(id, receivedItems, notes = '', receiptDate = '') {
  return apiClient.post(ENDPOINTS.PURCHASE_ORDERS.RECEIVE(id), {
    receiptDate,           // ✅ required by backend
    items: receivedItems,  // ✅ backend expects "items" not "receivedItems"
    notes,
  });
},

  /** Upload Excel/CSV to parse line items. */
  async uploadExcel(formData) {
    return apiClient.post(ENDPOINTS.PURCHASE_ORDERS.UPLOAD_EXCEL, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /** Upload invoice image (OCR). */
  async uploadImage(formData) {
    return apiClient.post(ENDPOINTS.PURCHASE_ORDERS.UPLOAD_IMAGE, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /**
   * AI-extract a draft PO from a supplier invoice/quotation (image or PDF).
   * Returns { supplierName, supplierGstin, orderDate, notes, matchedSupplierId,
   *           supplierMatches[], items[{ itemName, quantity, uom, unitPrice,
   *           matchedInventoryItemId, matchConfidence, matches[] }] }.
   */
  async extractFromImage(formData) {
    return apiClient.post(ENDPOINTS.PURCHASE_ORDERS.EXTRACT, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /**
   * Fetch suppliers list.
   * Uses ENDPOINTS.SUPPLIERS.GET_ALL → /api/inventory/suppliers
   */
  async getSuppliers() {
    return apiClient.get(ENDPOINTS.SUPPLIERS.GET_ALL);
  },

  /**
   * Fetch aggregate PO stats.
   * Uses the dedicated STATS endpoint → /api/inventory/purchase-orders/stats
   */
  async getStats(branchId) {
    const params = {};
    if (branchId) params.branchId = branchId;
    return apiClient.get(ENDPOINTS.PURCHASE_ORDERS.STATS, { params });
  },
};

export default purchaseOrderService;